import React, { useState, useEffect } from 'react';
import { 
  PhoneIcon, 
  EnvelopeIcon, 
  CheckCircleIcon,
  WrenchScrewdriverIcon 
} from '@heroicons/react/24/outline';
import SEO from '../components/SEO';

interface GalleryImage {
  id: number;
  src: string;
  title: string;
  category: string;
  description?: string;
  project_name?: string;
}

const Gallery: React.FC = () => {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [galleryImages, setGalleryImages] = useState<GalleryImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState('All');

  // Fetch gallery images from Laravel API
  useEffect(() => {
    const fetchGalleryImages = async () => {
      try {
        setLoading(true);
        const response = await fetch('https://plencoplumbingltd.com/api/gallery');
        
        if (!response.ok) {
          throw new Error('Failed to fetch gallery images');
        }
        
        const result = await response.json();
        
        if (result.success && result.data) {
          setGalleryImages(result.data);
        } else {
          throw new Error('Invalid response format');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        console.error('Error fetching gallery:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchGalleryImages();
  }, []);

  // Extract unique categories from fetched images
  const categories = ['All', ...Array.from(new Set(galleryImages.map(img => 
    img.project_name || img.category
  )))].filter(Boolean);

  const filteredImages = activeCategory === 'All' 
    ? galleryImages 
    : galleryImages.filter(img => 
        (img.project_name === activeCategory || img.category === activeCategory)
      );

  return (
    <div className="bg-white">      <SEO 
        title="Gallery - Project Photos | Plenco Plumbing & Construction Uganda"
        description="View photos of Plenco's construction and plumbing projects in Uganda. Browse our gallery featuring completed residential buildings, commercial projects, tank calibration work, fuel stations, renovation projects, and more across East Africa."
        keywords="construction photos Uganda, plumbing projects gallery, project portfolio Kampala, building photos Uganda, renovation photos, fuel station construction photos, tank calibration images"
        canonicalUrl="https://www.plencoplumbingltd.com/gallery"
        type="website"
      />
            {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary to-primary-700 text-white pt-32 sm:pt-36 pb-16 sm:pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-black mb-4">
            <span className="text-white">Our</span>{' '}
            <span className="text-primary-200">Gallery</span>
          </h1>
          <p className="text-xl text-primary-100 max-w-3xl mx-auto">
            Explore our portfolio of completed projects and ongoing works across Uganda and East Africa
          </p>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-8 border-b border-gray-200 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-4">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-6 py-2 rounded-lg font-semibold transition-all duration-200 ${
                  activeCategory === category
                    ? 'bg-primary text-white shadow-md'
                    : 'bg-white text-gray-700 hover:bg-primary-50 hover:text-primary border border-gray-300'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading && (
            <div className="text-center py-20">
              <div className="inline-block animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-primary"></div>
              <p className="mt-4 text-gray-600 text-lg">Loading gallery images...</p>
            </div>
          )}

          {error && (
            <div className="text-center py-20">
              <div className="bg-red-50 border border-red-200 rounded-lg p-8 max-w-lg mx-auto">
                <p className="text-red-600 text-lg font-semibold mb-2">Error Loading Gallery</p>
                <p className="text-red-500">{error}</p>
                <button 
                  onClick={() => window.location.reload()}
                  className="mt-4 px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                  Retry
                </button>
              </div>
            </div>
          )}

          {!loading && !error && filteredImages.length === 0 && (
            <div className="text-center py-20">
              <p className="text-gray-500 text-lg">No images found in this category.</p>
            </div>
          )}

          {!loading && !error && filteredImages.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {filteredImages.map((image, index) => (
                <div
                  key={image.id || index}
                  className="group relative overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer bg-gray-100"
                  onClick={() => setSelectedImage(image.src)}
                >
                  <div className="aspect-w-16 aspect-h-12 relative h-64">
                    <img
                      src={image.src}
                      alt={image.title}
                      className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-300"
                      onError={(e) => {
                        e.currentTarget.src = 'https://via.placeholder.com/600x400/035e85/ffffff?text=' + encodeURIComponent(image.title);
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                        <h3 className="text-lg font-bold">{image.title}</h3>
                        <p className="text-sm text-gray-200">{image.project_name || image.category}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Modal for Full Image */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-5xl max-h-full">
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute -top-12 right-0 text-white text-4xl hover:text-gray-300"
            >
              ×
            </button>
            <img
              src={selectedImage}
              alt="Full size"
              className="max-w-full max-h-screen object-contain"
            />
          </div>
        </div>
      )}

      {/* CTA Section */}
      <section className="relative bg-gradient-to-br from-primary to-primary-700 py-20 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Main CTA Content */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-full mb-6">
              <WrenchScrewdriverIcon className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6">
              Ready to Start Your Project?
            </h2>
            <p className="text-xl text-primary-100 max-w-3xl mx-auto mb-8 leading-relaxed">
              Transform your vision into reality with our expert team. From initial consultation to final execution, 
              we deliver excellence in every project, big or small.
            </p>
          </div>

          {/* Feature Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 hover:bg-white/15 transition-all duration-300">
              <CheckCircleIcon className="w-10 h-10 text-primary-200 mb-4" />
              <h3 className="text-lg font-bold text-white mb-2">Quality Guaranteed</h3>
              <p className="text-primary-100 text-sm">
                Professional workmanship with attention to every detail
              </p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 hover:bg-white/15 transition-all duration-300">
              <CheckCircleIcon className="w-10 h-10 text-primary-200 mb-4" />
              <h3 className="text-lg font-bold text-white mb-2">Timely Delivery</h3>
              <p className="text-primary-100 text-sm">
                Projects completed on schedule without compromising quality
              </p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 hover:bg-white/15 transition-all duration-300">
              <CheckCircleIcon className="w-10 h-10 text-primary-200 mb-4" />
              <h3 className="text-lg font-bold text-white mb-2">Expert Team</h3>
              <p className="text-primary-100 text-sm">
                Experienced professionals across all construction disciplines
              </p>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a
              href="/contact"
              className="group inline-flex items-center gap-2 bg-white text-primary px-8 py-4 rounded-lg font-bold hover:bg-primary-50 transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-105"
            >
              <EnvelopeIcon className="w-5 h-5" />
              <span>Get a Free Quote</span>
            </a>
            <a
              href="tel:+256200906397"
              className="group inline-flex items-center gap-2 bg-primary-800 text-white px-8 py-4 rounded-lg font-bold hover:bg-primary-900 transition-all duration-300 border-2 border-white/30 hover:border-white/50"
            >
              <PhoneIcon className="w-5 h-5" />
              <span>Call Us Now</span>
            </a>
          </div>

          {/* Trust Badge */}
          <div className="text-center mt-12">
            <p className="text-primary-100 text-sm">
              Trusted by clients across Uganda and East Africa • Over 15 years of excellence
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Gallery;
